import { useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import css from '@styled-system/css';
import { useRouter } from 'next/router';

import { contentfulFormType, contentfulImageType } from 'types';
import getContentfulImgixUrl from 'utils/getContentfulImgixUrl';
import useRollbar from 'hooks/useRollbar';
import Section from 'legacy/components/section';
import Flex from 'legacy/components/flex';
import Box from 'legacy/components/box';
import Heading from 'legacy/components/heading';
import Image from 'legacy/components/image';
import Card from 'legacy/components/card';
import ContentfulForm from 'legacy/components/contentfulForm';
import { useHubspotForm } from 'hooks/useHubspotForm';

const StyledSection = styled(Section)(
  css({
    backgroundImage: 'url("/images/grayEllipse.svg")',
    backgroundSize: ['cover', null, null, 'contain'],
    backgroundRepeat: 'no-repeat',
    // Need to add more bottom padding at these sizes so the image has enough room
    // to expand to the edge
    '@media screen and (min-width: 87em)': {
      pb: 8,
    },
    '@media screen and (min-width: 91.5em)': {
      pb: 176,
    },
    // contain starts to clip the image here
    '@media screen and (min-width: 99em)': {
      backgroundSize: 'cover',
    },
  })
);

const StyledImage = styled(Image)(
  css({
    borderRadius: 'large',
    '& img': {
      borderRadius: 'large',
    },
  })
);

const GuideGatedMasthead = ({ heading, image, form, children, ...props }) => {
  const [isLoading, setIsLoading] = useState(false);
  const rollbarClient = useRollbar();
  const router = useRouter();
  const { asPath } = router;
  const { submitForm: submitHubspotForm } = useHubspotForm(
    form.hubspotFormId,
    form.hubspotSandboxFormId,
    form.salesforceCampaignId,
    form.formType
  );

  const handleSubmit = async (values) => {
    setIsLoading(true);

    try {
      await submitHubspotForm(values);

      router.push(`${window?.location.pathname}thanks/`);
    } catch (e) {
      rollbarClient.error(`Error creating lead from ${asPath}`, e);
    }
  };

  return (
    <StyledSection variant="medium" {...props}>
      <Section.Inner>
        <Flex flexWrap="wrap">
          <Box width={[1, null, null, '49%']}>
            <StyledImage
              src={getContentfulImgixUrl(image.image?.url)}
              width={584}
              height={164}
              additionalParams={{ fit: 'crop' }}
              htmlAttributes={{ alt: image.altText }}
              lazyload
            />
            <Heading as="h1" variant="h3" mt={3}>
              {heading}
            </Heading>
            <Box mt={3}>{children}</Box>
          </Box>
          <Box
            width={[1, null, null, '40%']}
            mt={[5, null, null, 0]}
            ml={[0, null, null, '11%']}
          >
            <Card p={4} bg="white" boxShadow="card">
              <Heading as="h3" fontSize={2} fontWeight={700} mb={3}>
                {form.title}
              </Heading>
              <ContentfulForm
                inputs={form.inputsCollection?.items}
                onSubmit={handleSubmit}
                submitButtonText={form.submitButtonText}
                submitButtonVariant="secondary"
                isLoading={isLoading}
                disclosures={form.disclosures}
              />
            </Card>
          </Box>
        </Flex>
      </Section.Inner>
    </StyledSection>
  );
};

GuideGatedMasthead.propTypes = {
  heading: PropTypes.string.isRequired,
  image: contentfulImageType.isRequired,
  form: contentfulFormType.isRequired,
  children: PropTypes.node,
};

GuideGatedMasthead.defaultProps = {
  children: '',
};

export default GuideGatedMasthead;
